/* Light Armor — option wording for the Bold Options skin (split out 2026-08-27).
 *
 * These dictionaries are just words: what an option value is called, the one-line
 * blurb under it, and the plain-language explainer behind the (i). They change far
 * more often than the skin's behaviour does, and they were roughly a fifth of
 * la-buy-skin.js — so they live here, and a wording fix is a small file to push
 * instead of the whole 46KB skin.
 *
 * On a product page snippets/la-buy-top.liquid loads this with a plain tag, ahead of the
 * deferred skin. On the cart — where Bold's "Edit Order" lightbox is also skinned — no such
 * snippet renders, so la-buy-skin.js injects this file itself and reads the dictionaries
 * lazily, at lookup time. If it ever fails to load, the skin falls back to empty maps:
 * option values then show Bold's own labels with no blurb and no (i), which is plain but
 * correct — nothing breaks.
 *
 * Keys are matched lowercased and by prefix, longest key wins (see lookup() in
 * la-buy-skin.js), so 'standard element' also catches 'standard elements (x2)'.
 */
(function () {
  'use strict';


  /* Short marketing copy for known option values (prefix match, case-insensitive). */
  var COPY = {
    'standard elements': 'Standard Heat Up Time',
    'standard element': 'Standard Heat Up Time',
    'element upgrade': 'Faster heat-up times',
    'standard oven insulation': 'Fiberglass',
    'standard insulation': 'Fiberglass',
    'r15 rockwool': 'Thicker material · lower electric bill',
    'diy wiring': 'You hardwire from breaker to oven',
    'plug and play package': 'Cord + inlet + wall receptacle',
    'plug and play kit': 'Cord + inlet + wall receptacle',
    'flat base': 'Sits flat on floor',
    'stationary base': 'Sits flat on the floor',
    'insulated base': 'Adds 4" of insulated base',
    'mobile insulated base': 'Insulated base + casters',
    'mobile base': 'Insulated base + casters',
    'no blower fan': 'Heat rises to the top',
    'convection blower fan': 'Blends the temperature',
    'blower fan': 'Blends the temperature',
    'two blower fans': 'Blends it faster, larger loads',
    'double blower fan': 'Blends it faster, larger loads',
    'three blower fans': 'Even temps across a big chamber',
    'four blower fans': 'Even temps across a big chamber',
    'five blower fans': 'Even temps across a big chamber',
    'six blower fans': 'Even temps across a big chamber',
    'seven blower fans': 'Even temps across a big chamber',
    'eight blower fans': 'Even temps across a big chamber',
    'no window': '',
    'window and light upgrade': 'See parts without opening the door',
    'peep door and light': 'Small door + light',
    'no side lighting': 'Included',
    'side lighting upgrade': 'Two lights on each side wall',
    'no odor control': 'Fine for powder coating',
    'odor control kit': 'Great for Cerakote',
    'double odor control kit': 'Great for Cerakote',
    'cardboard and plastic wrap': 'Wrapped and strapped to a pallet',
    'wooden crate': 'Best protection during transit',
    'one-year parts warranty': 'Included',
    'two-year parts warranty': 'Parts coverage',
    'three-year parts warranty': 'Parts coverage',
    'five-year parts warranty': 'Parts coverage',
    'no kit': '',
    'no kits': '',
    'single-phase 240v': '2 hots, 1 neutral, 1 ground',
    'three-phase 208v': '3 hots, 1 neutral',
    'vent kit (for venting fumes out - includes adapter frame': 'Adapter frame, ducting and inline fan',
    'vent kit (for venting fumes out - includes two adapter frames': 'Two frames, ducting and inline fans',
    'vent kits (for venting fumes out': 'Frames, ducting and inline fans'
  };
  var SHORT = {
    'blower fan (consistent temperatures from top to bottom)': 'One Blower Fan',
    'two blower fans (consistent temperatures from top to bottom)': 'Two Blower Fans',
    'double blower fan (consistent temperatures from top to bottom)': 'Double Blower Fan',
    /* Bold's stored value still reads "…and Vented System"; display-only override
       until Shop Circle's backend serves the corrected label. */
    'convection blower fan and vented system': 'One Blower Fan',
    'convection blower fan': 'One Blower Fan',
    'three blower fans': 'Three Blower Fans',
    'four blower fans': 'Four Blower Fans',
    'five blower fans': 'Five Blower Fans',
    'six blower fans': 'Six Blower Fans',
    'seven blower fans': 'Seven Blower Fans',
    'eight blower fans': 'Eight Blower Fans',
    'no window or peep door and light': 'No Upgrade',
    'flat base (non-insulated bottom)': 'Flat Base',
    'stationary base': 'Stationary Base',
    'element upgrade (faster heat up times)': 'Element Upgrade',
    'r15 rockwool (holds heat in better and saves on electricity)': 'R15 Rockwool',
    'plug and play package (power cord + wired inlet + wall receptacle)': 'Plug & Play Package',
    'plug and play kit (includes controller box inlet wired up + power cord + wall receptacle)': 'Plug & Play Kit',
    'built to order': 'Built to Order',
    'build to order': 'Built to Order',
    'expedite order': 'Expedite',
    'single-phase 240v': 'Single-Phase 240V',
    'three-phase 208v': 'Three-Phase 208V',
    'vent kit (for venting fumes out - includes two adapter frames': 'Vent Kits (×2)',
    'vent kits': 'Vent Kits',
    'vent kit': 'Vent Kit',
    'wooden crate (insurance included)': 'Wooden Crate',
    'cardboard and plastic wrap': 'Cardboard Wrapped',
    'window and light upgrade': 'Window + Light',
    'peep door and light': 'Peep Door',
    'mobile insulated base': 'Mobile Insulated',
    'mobile base': 'Mobile Insulated',
    'standard oven insulation': 'Standard Insulation',
    'standard elements': 'Standard Element(s)',
    'one-year parts warranty': '1 Year',
    'two-year parts warranty': '2 Years',
    'three-year parts warranty': '3 Years',
    'five-year parts warranty': '5 Years',
    'side lighting upgrade': 'Side Lighting Upgrade',
    'no side lighting': 'No Side Lighting',
    'no odor control': 'No Odor Control',
    'odor control kit': 'Odor Control Kit',
    'double odor control kit': 'Double Odor Control Kit'
  };
  /* Bold's option headings vary between products; show one wording sitewide. */
  var HEADS = { 'convection blower fan upgrade': 'Convection Blower Fan' };

  /* Most LABS ovens carry their fans on the left wall only; these models can take
     them on both side walls. */
  var LABS_BOTH_WALLS = /LABS[-\s]?(866|867|886|887|888)\b/i;
  function blowerTip(productTitle) {
    var t = 'Heat rises, so the top of the oven runs hotter than the bottom — which can change how parts cure depending on their size, thickness and where they sit. Our vented blower system pulls the heat building up at the top down through a side-wall vent and back into the chamber, blending the temperature throughout.';
    if (/\bLABS\b/i.test(productTitle || '')) {
      return t + (LABS_BOTH_WALLS.test(productTitle)
        ? ' On this model the fans can be fitted to either side wall.'
        : ' On this model the fans are fitted to the left side wall, opposite the control box.');
    }
    return t + ' The first fan sits on the control box wall; a second goes on the opposite wall for even better uniformity.';
  }

  /* Plain-language explainers, shown behind an (i) beside the option heading. */
  var TIPS = {
    'insulation type': "Rockwool holds its shape and won't sag over time, and it keeps heat in at a better rate — so the oven draws less power to hold temperature, saving you money long term.",
    'heating element': 'The upgraded element(s) are larger and produce more heat while drawing slightly higher amperage, so the oven reaches a given set temperature much quicker.',
    'electrical configuration': 'The Plug & Play package includes an inlet wired up on the oven for one end of the power cord to plug into, and a wall receptacle for the other end to plug into.',
    'turnaround time': "This is build time only — it doesn't include transit, which can add a few days or more depending on your location and driver availability.",
    'complete parts warranty': "Covers all electrical parts and components — relay, elements, PID controller, fans and so on. If a part goes bad, just let us know and we'll ship a replacement out as soon as possible, no hassle.",
    'freight preparation': 'Cardboard wrap protects the unit in most cases, but damage does happen from time to time — sometimes minor, sometimes major. If your order needs to arrive in pristine condition, choose the crate. We can also look at hiring a private truck driver, which is often the same cost as LTL plus crating (sometimes less) and is the most secure way to ship.',
    'oven window or peep door': 'The window and light lets you check on curing parts without opening the door. The peep door and light does the same, and also lets you take part temperatures with a temp gun while the oven stays closed.',
    'oven base type': 'Flat base works great on concrete and is the one to choose if you roll carts into the oven. Insulated base seals heat in better and suits sensitive floors such as epoxy or wood. Mobile insulated base is worth it if you move the oven around the shop regularly. Caster wheels ship uninstalled and screw onto the bottom of the oven with the screws provided.',
    /* Fan placement differs by model, so this one is resolved per product. */
    'convection blower fan': blowerTip,
    'blower fan': blowerTip
  };
  window.__laSkinCopy = { COPY: COPY, SHORT: SHORT, HEADS: HEADS, TIPS: TIPS };
}());
